import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import Enumerable from "linq";
import { DataTypeEnum } from "../../common/enums/DataTypeEnum";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import * as DRCX from "../../common/enums/DRCXEnums";
import { AccessType, ConfigurationType, Scope, Style, UsageType } from "../../common/enums/DRCXEnums";
import { eLynxAppIDEnum } from "../../common/enums/eLynxAppIDEnum";
import { NumericalFunctionEnum } from "../../common/enums/NumericalFunctionEnum";
import { AccessPermission, IAccessPermission } from "../../common/models/AccessPermission";
import { DRCXAvailableOptionalColumn } from "../../common/models/drcx/DRCXAvailableOptionalColumn";
import { IDRCXConfigurationDTO } from "../../common/models/drcx/DRCXConfigurationDTO";
import { DRCXConfigurationModel } from "../../common/models/drcx/DRCXConfigurationModel";
import { DRCXConfigurationSectionModel } from "../../common/models/drcx/DRCXConfigurationSectionModel";
import { DRCXConfigurationSectionObjectModel } from "../../common/models/drcx/DRCXConfigurationSectionObjectModel";
import { IDRCXConfigurationBase } from "../../common/models/drcx/IDRCXConfigurationBase";
import { IEquatable } from "../../common/models/IEquatable";
import i18n from "../localization-service/i18n";
import { SummaryListingSortTypeEnum } from "../../common/enums/SummaryListingSortTypeEnum";

export enum PageConfigurationTypeEnum {
    None = 0,
    SummaryListing_MeterDOC = 1,
    DeviceDetail_MeterDOC = 2,
    TagDetail_MeterDOC = 3,
    SummaryListing_RTS = 4,
    DeviceDetail_RTS = 5,
    TagDetail_RTS = 6,
    InjectionModeling = 7,
    SiteListing_RTS = 8,
    SummaryListing_RTS_Well = 9
}

export enum PageConfigurationAccessEnum {
    None = 0,
    Private = 1,
    Shared = 2,
    SharedReadOnly = 3
}

export interface IPageConfigurationQueryParams {
    pageConfigurationID: number;
}

export interface IPageConfigurationDefaultQueryParams {
    pcType: PageConfigurationTypeEnum;
}

export interface IPageConfigurationTypeQueryParams {
    pcType: PageConfigurationTypeEnum;
    deviceType?: DeviceTypesEnum
}

// #region Summary Listing Configuration Models

export interface ISummaryListingConfigurationResults {
    configurationId: number;
    configurationChanged: boolean;
    configurationRenamed: boolean;
    permissionsModified: boolean;
}

// #endregion Summary Listing Configuration Models

// #region Page Configuration Version 1 Models

export interface IPageConfigurationAvailableTatColumn {
    tatID: number;
    tatName: string;
    dataType: DataTypeEnum;    
    summaryFunction?: NumericalFunctionEnum;
}

export interface IPageConfigurationAvailableOptionalColumn {
    default?: boolean;
    displayName?: string;
    isSelected?: boolean;
    key?: string;
    name?: string;
    optionalColumn?: DRCX.OptionalColumnEnum;
    order?: number;
    required?: boolean;

    id?: number;

}

export class PageConfigurationAvailableOptionalColumn implements IPageConfigurationAvailableOptionalColumn {
    default?: boolean;
    displayName?: string;
    isSelected?: boolean;
    key?: string;
    name?: string;
    optionalColumn?: DRCX.OptionalColumnEnum;
    order?: number;
    required?: boolean;

    id?: number;

    constructor(object?: IPageConfigurationAvailableOptionalColumn) {

        if (object) {
            this.default = object.default;
            this.displayName = object.displayName;
            this.isSelected = object.isSelected;
            this.key = object.key;
            this.name = object.name;
            this.optionalColumn = object.optionalColumn;
            this.order = object.order;
            this.required = object.required;
            this.id = object.id;
        }
    }
}

export interface IPageConfigurationDefaultModel {
    configurationID?: number;
    defaultForUser?: boolean;
    defaultForGroup?: boolean;
    defaultForAllUsers?: boolean;
}

// TODO: Need class?
export class PageConfigurationDefaultModel implements IPageConfigurationDefaultModel {
    configurationID?: number;
    defaultForUser?: boolean;
    defaultForGroup?: boolean;
    defaultForAllUsers?: boolean;
}

export interface IPageConfigurationDefaultOptionsModel {
    defaultForUser?: boolean;
    defaultForGroup?: boolean;
    defaultForAllUsers?: boolean;
}

// TODO: Need class?
export class PageConfigurationDefaultOptionsModel implements IPageConfigurationDefaultOptionsModel {
    defaultForUser?: boolean;
    defaultForGroup?: boolean;
    defaultForAllUsers?: boolean;
}

// #endregion Page Configuration Version 1 Models

// #region Page Configuration Version 3 Models

export interface IPageConfigurationDataModel3 {
    defaultPageConfigurationId?: number;
    organizationDescription?: string;
    groupDescription?: string;
    userPermissions?: IAccessPermission[];
    privateConfigurations?: IPageConfigurationModel3[];
    sharedConfigurations?: IPageConfigurationModel3[];
}

export class PageConfigurationDataModel3 implements IPageConfigurationDataModel3 {
    public defaultPageConfigurationId?: number;
    public groupDescription?: string;
    public organizationDescription?: string;
    public userPermissions?: AccessPermission[];
    public privateConfigurations?: PageConfigurationModel3[];
    public sharedConfigurations?: PageConfigurationModel3[];

    constructor(pageConfigurationData: IPageConfigurationDataModel3) {
        if (pageConfigurationData) {
            this.defaultPageConfigurationId = pageConfigurationData.defaultPageConfigurationId;
            this.organizationDescription = pageConfigurationData.organizationDescription;
            this.groupDescription = pageConfigurationData.groupDescription;
            this.userPermissions = pageConfigurationData.userPermissions?.map((permission: IAccessPermission): AccessPermission => {
                return new AccessPermission(permission);
            });
            this.privateConfigurations = pageConfigurationData.privateConfigurations?.map((configuration: IPageConfigurationModel3): PageConfigurationModel3 => {
                return new PageConfigurationModel3(configuration);
            });
            this.sharedConfigurations = pageConfigurationData.sharedConfigurations?.map((configuration: IPageConfigurationModel3): PageConfigurationModel3 => {
                return new PageConfigurationModel3(configuration);
            });
        }
    }
}

export interface IPageConfigurationModel3 extends IDRCXConfigurationBase {
    access?: PageConfigurationAccessEnum;
    userPermissions?: IAccessPermission[];
    pcType?: PageConfigurationTypeEnum;

    eLynxAppID?: eLynxAppIDEnum;

    columns?: IPageConfigurationColumn3[];
    lockedColumnCount?: number;

    sectionCount?: number;

    createdByUserID?: number;
    createdByUserName?: string;
    createdTimestamp?: Date;

    modifiedByUserID?: number;
    modifiedByUserName?: string;
    modifiedTimestamp?: Date;

    executedByUserID?: number;
    executedByUserName?: string;
    executedTimestamp?: Date;

    isGroupDefault?: boolean;
    isUserDefault?: boolean;
    isOrganizationDefault?: boolean;

    hidden?: boolean;
    hiddenByUserID?: number;
    hiddenByUserName?: string;
    hiddenTimestamp?: Date;

}

export class PageConfigurationModel3 implements IPageConfigurationModel3 {
    public static NEW_CONFIGURATION_ID: number = -1;

    // IDRCXConfigurationBase
    public id?: number;
    public name?: string;
    public configurationType?: DRCX.ConfigurationType = DRCX.ConfigurationType.SummaryListing;

    // IPageConfigurationModel3
    public access?: PageConfigurationAccessEnum;
    public userPermissions?: Array<AccessPermission>;
    public pcType?: PageConfigurationTypeEnum = PageConfigurationTypeEnum.SummaryListing_RTS;

    public columns?: Array<PageConfigurationColumn3>;
    public lockedColumnCount?: number;

    public sectionCount?: number;

    public createdByUserID?: number;
    public createdByUserName?: string;
    public createdTimestamp?: Date;

    public modifiedByUserID?: number;
    public modifiedByUserName?: string;

    public modifiedTimestamp?: Date;
    public executedByUserID?: number;
    public executedByUserName?: string;
    public executedTimestamp?: Date;

    public isGroupDefault?: boolean;
    public isUserDefault?: boolean;
    public isOrganizationDefault?: boolean;

    public hidden?: boolean;
    public hiddenByUserID?: number;
    public hiddenByUserName?: string;
    public hiddenTimestamp?: Date;


    constructor(pageConfiguration?: IPageConfigurationModel3, clone?: boolean) {
        if (pageConfiguration) {
            this.id = pageConfiguration.id;
            this.name = pageConfiguration.name;
            this.access = pageConfiguration.access;
            this.columns = pageConfiguration.columns?.sort((left, right) => (left.order ?? 0) - (right.order ?? 0)).map((column) => {
                return new PageConfigurationColumn3(column);
            });
            this.userPermissions = pageConfiguration.userPermissions?.map(permission => {
                return new AccessPermission(permission);
            });
            this.lockedColumnCount = pageConfiguration.lockedColumnCount;
            this.pcType = pageConfiguration.pcType ? pageConfiguration.pcType : PageConfigurationTypeEnum.SummaryListing_RTS;
            this.sectionCount = pageConfiguration.sectionCount;

            this.createdByUserID = pageConfiguration.createdByUserID;
            this.createdByUserName = pageConfiguration.createdByUserName;
            this.createdTimestamp = pageConfiguration.createdTimestamp;

            this.modifiedByUserID = pageConfiguration.modifiedByUserID;
            this.modifiedByUserName = pageConfiguration.modifiedByUserName;
            this.modifiedTimestamp = pageConfiguration.modifiedTimestamp;

            this.executedByUserID = pageConfiguration.executedByUserID;
            this.executedByUserName = pageConfiguration.executedByUserName;
            this.executedTimestamp = pageConfiguration.executedTimestamp;
            this.isGroupDefault = pageConfiguration.isGroupDefault;
            this.isUserDefault = pageConfiguration.isUserDefault;
            this.isOrganizationDefault = pageConfiguration.isOrganizationDefault,

            this.hidden = pageConfiguration.hidden;
            this.hiddenByUserID = pageConfiguration.hiddenByUserID;
            this.hiddenByUserName = pageConfiguration.hiddenByUserName;
            this.hiddenTimestamp = pageConfiguration.hiddenTimestamp;
        }
    }

    public userHasPermission(permission: AccessPermission): boolean {
        return !!this.userPermissions?.find(p => p.contains(permission));
    }

    public addColumn(name: string, equatableKey: string, isNew: boolean = false): void {
        if (!this.columns?.find((col: PageConfigurationColumn3) => col.equatableKey === equatableKey)) {
            var pageConfigurationItem: PageConfigurationColumn3 = new PageConfigurationColumn3({
                name: name,
                order: this.columns?.length,
            });
            pageConfigurationItem.equatableKey = equatableKey;
            pageConfigurationItem.isNew = isNew;

            this.columns?.push(pageConfigurationItem);
        }
    }

    public removeColumn(equatableKey: string) {
        // Find the item
        var itemToRemove = this.columns?.find((col: PageConfigurationColumn3) => col.equatableKey === equatableKey);

        // Did we find it?
        if (itemToRemove) {
            var index = this.columns?.indexOf(itemToRemove) ?? -1;
            if (index > -1) {
                this.columns?.splice(index, 1);

                // Reset the column orders to remove gaps
                this.columns?.forEach((column, index) => column.order = index);
            }

        }
    }

    public removeColumnsByTatID(tatID: number) {
        // Find the item
        var itemsToRemove = this.columns?.filter((col: PageConfigurationColumn3) => col.tatID === tatID);

        // Did we find any?
        if ((itemsToRemove?.length ?? 0) > 0) {
            itemsToRemove?.forEach(itemToRemove => {
                var index = this.columns?.indexOf(itemToRemove) ?? -1;
                if (index > -1) {
                    this.columns?.splice(index, 1);

                    // Reset the column orders to remove gaps
                    this.columns?.forEach((column, index) => column.order = index);
                }
            });
        }
    }

    public get optionalColumns(): Array<PageConfigurationColumn3> {
        return this.columns?.filter(column => column.columnType === DRCX.ColumnType.Optional) ??
            new Array<PageConfigurationColumn3>();
    }

    public get tatColumns(): Array<PageConfigurationColumn3> {
        return this.columns?.filter(column => column.columnType === DRCX.ColumnType.Tat) ??
            new Array<PageConfigurationColumn3>();

    }

    public get metaDataColumns(): Array<PageConfigurationColumn3> {
        return this.columns?.filter(column => column.columnType === DRCX.ColumnType.MetaData) ??
            new Array<PageConfigurationColumn3>();
    }

    public get tatAndMetaDataColumns(): Array<PageConfigurationColumn3> {
        return this.columns?.filter(column => column.columnType === DRCX.ColumnType.Tat || column.columnType === DRCX.ColumnType.MetaData) ??
            new Array<PageConfigurationColumn3>();
    }

    // Customize JSON.stringify to ignore transient properties
    public toJSON(): Object {
        return {
            id: this.id,
            name: this.name?.trim(),
            access: this.access,
            columns: this.columns,
            userPermissions: this.userPermissions,
            lockedColumnCount: this.lockedColumnCount,
            pcType: this.pcType
        };
    }
}



/////////////////////////
// export interface IPageConfigurationModel4 extends IDRCXConfigurationBase {
//     access?: PageConfigurationAccessEnum;
//     userPermissions?: IAccessPermission[];
//     pcType?: PageConfigurationTypeEnum;

//     sections: IPageConfigurationSection[];
//     // columns?: IPageConfigurationColumn3[];
//     // lockedColumnCount?: number;
// }

// export interface IPageConfigurationSection {
//     columns: IPageConfigurationColumn3[];
//     lockedColumnCount?: number;
// }
// export class PageConfigurationSection {

//     public columns: PageConfigurationColumn3[] = [];
//     public lockedColumnCount?: number;

//     constructor(pageConfiguration?: IPageConfigurationModel3, clone?: boolean) {
//         if (pageConfiguration) {
//             this.columns = pageConfiguration.columns?.sort((left, right) => (left.order ?? 0) - (right.order ?? 0)).map((column) => {
//                 return new PageConfigurationColumn3(column);
//             }) ?? [];
//             this.lockedColumnCount = pageConfiguration.lockedColumnCount;
//         }
//     }
// }


export interface IPageConfigurationModel4 extends IDRCXConfigurationDTO {
    pageConfigurationType: PageConfigurationTypeEnum;
    groupByAttributeID?: number;
    eLynxAppID: eLynxAppIDEnum;
    summaryListingSortType: SummaryListingSortTypeEnum;
    hidden: boolean;
}

export class PageConfigurationModel4 extends DRCXConfigurationModel {

    public pageConfigurationType: PageConfigurationTypeEnum = PageConfigurationTypeEnum.SummaryListing_RTS;
    groupByAttributeID?: number;    
    public eLynxAppID: eLynxAppIDEnum = eLynxAppIDEnum.eLynxApp;
    public summaryListingSortType: SummaryListingSortTypeEnum = SummaryListingSortTypeEnum.Alphanumeric;
    public hidden: boolean = false;

    public constructor(dto?: IPageConfigurationModel4) {
        super(dto);
        this.pageConfigurationType = dto?.pageConfigurationType ?? PageConfigurationTypeEnum.SummaryListing_RTS;
        this.groupByAttributeID = dto?.groupByAttributeID;
        this.eLynxAppID = dto?.eLynxAppID ?? eLynxAppIDEnum.eLynxApp;
        this.summaryListingSortType = dto?.summaryListingSortType ?? SummaryListingSortTypeEnum.Alphanumeric;
        this.hidden = dto?.hidden ?? false;

        Enumerable.from(this.sections)
            .selectMany(o => o.tatColumns)
            .select(o => {
                o.groupingFunction = NumericalFunctionEnum.None
            })
            .toArray();
    }



    public static createDefaultPageConfiguration(configurationID: number, 
        pageConfigurationType: PageConfigurationTypeEnum,
        optionalColumns: Enumerable.ILookup<string, DRCXAvailableOptionalColumn>)
        : PageConfigurationModel4 {

        const section = DRCXConfigurationSectionModel.createDefault(0, optionalColumns);

        const sectionObject = new DRCXConfigurationSectionObjectModel();
        sectionObject.objectType = eLynxObjectTypeEnum.Group;
        section.objects = [sectionObject];

        // add any default columns here
        const lookupKey: string = DRCXAvailableOptionalColumn.getLookupKey(Style.Pivot_Object_Only, pageConfigurationType === PageConfigurationTypeEnum.SummaryListing_RTS ? eLynxObjectTypeEnum.Device : eLynxObjectTypeEnum.Well);
        const column = optionalColumns.get(lookupKey).firstOrDefault(o => o.displayText === 'Device Name' || o.displayText === 'Well Name');

        if (column) {
            section.addColumn(column.displayText,
                column.equatableKey,
                column.usageType === UsageType.Default,
                column.usageType === UsageType.Required
            );
        }

        if (pageConfigurationType === PageConfigurationTypeEnum.SummaryListing_RTS_Well) {
            section.dataType = eLynxObjectTypeEnum.Well;
        }

        const configuration = new PageConfigurationModel4({
            id: configurationID,
            name: "New View",
            configurationType: ConfigurationType.SummaryListing,
            pageConfigurationType: pageConfigurationType,
            access: AccessType.Private,
            userPermissions: [],
            scope: Scope.User,
            created: new Date(),
            forAllGroups: false,
            overrideInstanceDates: false,
            eLynxAppID: eLynxAppIDEnum.eLynxApp,
            summaryListingSortType: SummaryListingSortTypeEnum.Alphanumeric,
            hidden: false,
            // timeframe: {
            //     durationTimeframeType: DurationTimeframeTypeEnum.MultipleTimeFrames,
            //     calculatedStartDateOffset: {
            //         duration: -6,
            //         rangeType: DateRangeType.Day
            //     },
            //     duration: {
            //         duration: 7,
            //         rangeType: DateRangeType.Day
            //     }
            // },
            sections: [
                section
            ]
        });

        //configuration.pageConfigurationType = pageConfigurationType;
    
        return configuration;
    }

}


// export class xPageConfigurationModel4 implements IPageConfigurationModel4 {

//     public static NEW_CONFIGURATION_ID: number = -1;

//     // IDRCXConfigurationBase
//     public id?: number;
//     public name?: string;
//     public configurationType?: DRCX.ConfigurationType = DRCX.ConfigurationType.SummaryListing;

//     // IPageConfigurationModel3
//     public access?: PageConfigurationAccessEnum;
//     public userPermissions?: Array<AccessPermission>;
//     public pcType?: PageConfigurationTypeEnum = PageConfigurationTypeEnum.SummaryListing_RTS;

//     public sections: PageConfigurationSection[] = [];

//     public columns?: Array<PageConfigurationColumn3>;
//     public lockedColumnCount?: number;

//     constructor(pageConfiguration?: IPageConfigurationModel4, clone?: boolean) {
//         if (pageConfiguration) {
//             this.id = pageConfiguration.id;
//             this.name = pageConfiguration.name;
//             this.access = pageConfiguration.access;
//             this.userPermissions = pageConfiguration.userPermissions?.map(permission => {
//                 return new AccessPermission(permission);
//             });
//             this.sections = pageConfiguration.sections?.map(v => new PageConfigurationSection(v, clone)) ?? [];
//             this.pcType = pageConfiguration.pcType ? pageConfiguration.pcType : PageConfigurationTypeEnum.SummaryListing_RTS;
//         }
//     }

//     public userHasPermission(permission: AccessPermission): boolean {
//         return !!this.userPermissions?.find(p => p.contains(permission));
//     }

//     public addColumn(name: string, equatableKey: string, isNew: boolean = false): void {
//         if (!this.columns?.find((col: PageConfigurationColumn3) => col.equatableKey === equatableKey)) {
//             var pageConfigurationItem: PageConfigurationColumn3 = new PageConfigurationColumn3({
//                 name: name,
//                 order: this.columns?.length,
//             });
//             pageConfigurationItem.equatableKey = equatableKey;
//             pageConfigurationItem.isNew = isNew;

//             this.columns?.push(pageConfigurationItem);
//         }
//     }

//     public removeColumn(equatableKey: string) {
//         // Find the item
//         var itemToRemove = this.columns?.find((col: PageConfigurationColumn3) => col.equatableKey === equatableKey);

//         // Did we find it?
//         if (itemToRemove) {
//             var index = this.columns?.indexOf(itemToRemove) ?? -1;
//             if (index > -1) {
//                 this.columns?.splice(index, 1);

//                 // Reset the column orders to remove gaps
//                 this.columns?.forEach((column, index) => column.order = index);
//             }
//         }
//     }

//     public removeColumnsByTatID(tatID: number) {
//         // Find the item
//         var itemsToRemove = this.columns?.filter((col: PageConfigurationColumn3) => col.tatID === tatID);

//         // Did we find any?
//         if ((itemsToRemove?.length ?? 0) > 0) {
//             itemsToRemove?.forEach(itemToRemove => {
//                 var index = this.columns?.indexOf(itemToRemove) ?? -1;
//                 if (index > -1) {
//                     this.columns?.splice(index, 1);

//                     // Reset the column orders to remove gaps
//                     this.columns?.forEach((column, index) => column.order = index);
//                 }
//             });
//         }
//     }

//     public get optionalColumns(): Array<PageConfigurationColumn3> {
//         return this.columns?.filter(column => column.columnType === DRCX.ColumnType.Optional) ??
//             new Array<PageConfigurationColumn3>();
//     }

//     public get tatColumns(): Array<PageConfigurationColumn3> {
//         return this.columns?.filter(column => column.columnType === DRCX.ColumnType.Tat) ??
//             new Array<PageConfigurationColumn3>();

//     }

//     public get metaDataColumns(): Array<PageConfigurationColumn3> {
//         return this.columns?.filter(column => column.columnType === DRCX.ColumnType.MetaData) ??
//             new Array<PageConfigurationColumn3>();
//     }

//     public get tatAndMetaDataColumns(): Array<PageConfigurationColumn3> {
//         return this.columns?.filter(column => column.columnType === DRCX.ColumnType.Tat || column.columnType === DRCX.ColumnType.MetaData) ??
//             new Array<PageConfigurationColumn3>();
//     }

//     // Customize JSON.stringify to ignore transient properties
//     public toJSON(): Object {
//         return {
//             id: this.id,
//             name: this.name?.trim(),
//             access: this.access,
//             columns: this.columns,
//             userPermissions: this.userPermissions,
//             lockedColumnCount: this.lockedColumnCount,
//             pcType: this.pcType
//         };
//     }
// }



////////////////

export interface IPageConfigurationColumn3 {
    columnType?: DRCX.ColumnType;
    name?: string;
    label?: string;
    order?: number;

    // columnType == DRCX.ColumnType.Optional
    optionalColumn?: DRCX.OptionalColumnEnum;
    key?: string;

    // columnType == DRCX.ColumnType.Tat || columnType == DRCX.ColumnType.MetaData
    tatID?: number;
    groupingTimeframe?: DRCX.TimeframeType;

    // columnType == DRCX.ColumnType.Tat
    aggregationType?: DRCX.AggregateType;
    aggregationPeriod?: number;
    aggregationFunction?: number;
    aggregationStepDateRange?: DRCX.DateRangeType;
    aggregationStep?: number;

    // columnType == DRCX.ColumnType.MetaData
    groupingFunction?: NumericalFunctionEnum;
    metaDataType?: DRCX.MetaDataType;
    startOffset?: number;
    startRangeType?: DRCX.DateRangeType;
    duration?: number;
    durationRangeType?: DRCX.DateRangeType;

    isDeprecated?: boolean;
}

export class PageConfigurationColumn3 implements IPageConfigurationColumn3, IEquatable<string> {

    public columnType?: DRCX.ColumnType = DRCX.ColumnType.Optional;
    public name?: string = "";
    public label?: string = "";
    public order?: number = 0;

    // columnType == DRCX.ColumnType.Optional
    public optionalColumn?: DRCX.OptionalColumnEnum;
    public key?: string = "";

    // columnType == DRCX.ColumnType.Tat || columnType == DRCX.ColumnType.MetaData
    public tatID?: number;
    public groupingTimeframe?: DRCX.TimeframeType;

    // columnType == DRCX.ColumnType.Tat
    public aggregationType?: DRCX.AggregateType;
    public aggregationPeriod?: number;
    public aggregationFunction?: NumericalFunctionEnum;
    public aggregationStepDateRange?: DRCX.DateRangeType;
    public aggregationStep?: number;

    // columnType == DRCX.ColumnType.MetaData
    public groupingFunction?: NumericalFunctionEnum;
    public metaDataType?: DRCX.MetaDataType;
    public startOffset?: number;
    public startRangeType?: DRCX.DateRangeType;
    public duration?: number;
    public durationRangeType?: DRCX.DateRangeType;

    public displayText?: string;
    public isSelected?: boolean;
    public isInvalid?: boolean;
    public isNew?: boolean = false;
    public isDeprecated?: boolean = false;

    constructor(pageConfigurationColumn?: IPageConfigurationColumn3) {

        if (pageConfigurationColumn) {

            this.columnType = pageConfigurationColumn.columnType;
            this.order = pageConfigurationColumn.order;
            this.name = pageConfigurationColumn.name ? pageConfigurationColumn.name.trim() : "";
            this.label = pageConfigurationColumn.label ? pageConfigurationColumn.label.trim() : "";
            this.isDeprecated = pageConfigurationColumn.isDeprecated;

            if (this.columnType === DRCX.ColumnType.Optional) {

                this.optionalColumn = pageConfigurationColumn.optionalColumn;
                this.key = pageConfigurationColumn.key;

            } else if (this.columnType === DRCX.ColumnType.Tat) {

                this.tatID = pageConfigurationColumn.tatID;
                this.groupingTimeframe = pageConfigurationColumn.groupingTimeframe;
                this.aggregationType = pageConfigurationColumn.aggregationType;
                this.aggregationPeriod = pageConfigurationColumn.aggregationPeriod;
                this.aggregationFunction = pageConfigurationColumn.aggregationFunction;
                this.aggregationStepDateRange = pageConfigurationColumn.aggregationStepDateRange;
                this.aggregationStep = pageConfigurationColumn.aggregationStep;

            } else { // this.columnType === DRCX.ColumnType.MetaData

                this.tatID = pageConfigurationColumn.tatID;
                this.groupingTimeframe = pageConfigurationColumn.groupingTimeframe;
                this.groupingFunction = pageConfigurationColumn.groupingFunction;
                this.metaDataType = pageConfigurationColumn.metaDataType;
                this.startOffset = pageConfigurationColumn.startOffset;
                this.startRangeType = pageConfigurationColumn.startRangeType;
                this.duration = pageConfigurationColumn.duration;
                this.durationRangeType = pageConfigurationColumn.durationRangeType;
            }

            this.displayText = this.name ?? i18n.t("pageConfiguration.invalidColumn");
        }
    }

    // IEquatable Implementation
    public get equatableKey(): string {
        return this.columnType === DRCX.ColumnType.Optional
            ? PageConfigurationColumn3.makeOptionalEquatableKey(
                this.optionalColumn,
                this.key)
            : this.columnType === DRCX.ColumnType.Tat
                ? PageConfigurationColumn3.makeTatEquatableKey(
                    this.tatID,
                    this.groupingTimeframe,
                    this.aggregationType,
                    this.aggregationPeriod,
                    this.aggregationFunction,
                    this.aggregationStepDateRange,
                    this.aggregationStep)
                : PageConfigurationColumn3.makeMetaDataEquatableKey(
                    this.tatID,
                    this.groupingTimeframe,
                    this.groupingFunction,
                    this.metaDataType,
                    this.startOffset,
                    this.startRangeType,
                    this.duration,
                    this.durationRangeType);
    }

    public set equatableKey(value: string) {
        const components: string[] = value.split(":");
        this.columnType = components.length > 0 && components[0].length > 0 ? Number(components[0]) : undefined;

        if (this.columnType === DRCX.ColumnType.Optional) {

            this.optionalColumn = components.length > 1 && components[1].length > 0 ? Number(components[1]) as DRCX.OptionalColumnEnum : undefined;
            this.key = components.length > 2 && components[2].length > 0 ? components[2] : undefined;

        } else if (this.columnType === DRCX.ColumnType.Tat) {

            this.tatID = components.length > 1 && components[1].length > 0 ? Number(components[1]) : undefined;
            this.groupingTimeframe = components.length > 2 && components[2].length > 0 ? Number(components[2]) : undefined;
            this.aggregationType = components.length > 3 && components[3].length > 0 ? Number(components[3]) as DRCX.AggregateType : undefined;
            this.aggregationPeriod = components.length > 4 && components[4].length > 0 ? Number(components[4]) : undefined;
            this.aggregationFunction = components.length > 5 && components[5].length > 0 ? Number(components[5]) as NumericalFunctionEnum : undefined;
            this.aggregationStepDateRange = components.length > 6 && components[6].length > 0 ? Number(components[6]) as DRCX.DateRangeType : undefined;
            this.aggregationStep = components.length > 7 && components[7].length > 0 ? Number(components[7]) : undefined;

        } else { // this.columnType == DRCX.ColumnType.MetaData

            this.tatID = components.length > 1 && components[1].length > 0 ? Number(components[1]) : undefined;
            this.groupingTimeframe = components.length > 2 && components[2].length > 0 ? Number(components[2]) : undefined;
            this.groupingFunction = components.length > 3 && components[3].length > 0 ? Number(components[3]) : undefined;
            this.metaDataType = components.length > 4 && components[4].length > 0 ? Number(components[4]) : undefined;
            this.startOffset = components.length > 5 && components[5].length > 0 ? Number(components[5]) : undefined;
            this.startRangeType = components.length > 6 && components[6].length > 0 ? Number(components[6]) : undefined;
            this.duration = components.length > 7 && components[7].length > 0 ? Number(components[7]) : undefined;
            this.durationRangeType = components.length > 8 && components[8].length > 0 ? Number(components[8]) : undefined;

        }
    }

    // Customize JSON.stringify to ignore transient properties
    public toJSON(): Object {
        return {
            columnType: this.columnType,
            order: Number(this.order),
            name: this.name?.trim() ?? "",
            label: this.label?.trim() ?? "",
            optionalColumn: this.optionalColumn,
            key: this.key,
            tatID: this.tatID,
            aggregationType: this.aggregationType,
            aggregationPeriod: this.aggregationPeriod,
            aggregationFunction: this.aggregationFunction,
            aggregationStepDateRange: this.aggregationStepDateRange,
            aggregationStep: this.aggregationStep,
            groupingTimeframe: this.groupingTimeframe,
            groupingFunction: this.groupingFunction,
            metaDataType: this.metaDataType,
            startOffset: this.startOffset,
            startRangeType: this.startRangeType,
            duration: this.duration,
            durationRangeType: this.durationRangeType
        };
    }

    public static makeOptionalEquatableKey(
        optionalColumn?: DRCX.OptionalColumnEnum,
        key?: string): string {
        return [
            DRCX.ColumnType.Optional,
            typeof optionalColumn == "number" ? optionalColumn : "",
            typeof key == "string" ? key : "",
        ].join(":");
    }

    public static makeTatEquatableKey(
        tatID?: number,
        groupingTimeframe?: DRCX.TimeframeType,
        aggregationType?: DRCX.AggregateType,
        aggregationPeriod?: number,
        aggregationFunction?: number,
        aggregationStepDateRange?: DRCX.DateRangeType,
        aggregationStep?: number): string {
        return groupingTimeframe === DRCX.TimeframeType.LastKnownValue
            ? [
                DRCX.ColumnType.Tat,
                typeof tatID == "number" ? tatID : "",
                typeof groupingTimeframe == "number" ? groupingTimeframe : "",
                typeof aggregationType == "number" ? aggregationType : "",
            ].join(":")
            : [
                DRCX.ColumnType.Tat,
                typeof tatID == "number" ? tatID : "",
                typeof groupingTimeframe == "number" ? groupingTimeframe : "",
                typeof aggregationType == "number" ? aggregationType : "",
                typeof aggregationPeriod == "number" ? aggregationPeriod : "",
                typeof aggregationFunction == "number" ? aggregationFunction : "",
                typeof aggregationStepDateRange == "number" ? aggregationStepDateRange : "",
                typeof aggregationStep == "number" ? aggregationStep : ""
            ].join(":");
    }

    public static makeMetaDataEquatableKey(
        tatID?: number,
        groupingTimeframe?: DRCX.TimeframeType,
        groupingFunction?: NumericalFunctionEnum,
        metaDataType?: DRCX.MetaDataType,
        startOffset?: number,
        startRangeType?: DRCX.DateRangeType,
        duration?: number,
        durationRangeType?: DRCX.DateRangeType): string {
        return [
            DRCX.ColumnType.MetaData,
            typeof tatID === "number" ? tatID : "",
            typeof groupingTimeframe === "number" ? groupingTimeframe : "",
            typeof groupingFunction === "number" ? groupingFunction : "",
            typeof metaDataType === "number" ? metaDataType : "",
            typeof startOffset === "number" ? startOffset : "",
            typeof startRangeType === "number" ? startRangeType : "",
            typeof duration === "number" ? duration : "",
            typeof durationRangeType === "number" ? durationRangeType : ""
        ].join(":");
    }
}

export interface IPageConfigurationAvailableColumns3 {
    sparklineCount: number;
    attributeColumns: Array<IPageConfigurationAvailableOptionalColumn>;
    tatColumns: Array<IPageConfigurationAvailableTatColumn>;
}

// #endregion Page Configuration Version 3 Models